/**
 * Price Formatting Utilities
 *
 * Web-specific price formatting functions.
 * Uses Intl.NumberFormat for locale-aware formatting.
 *
 * @module @kidslabs/module-flash-sale/utils/price
 */

export interface FormatPriceParams {
    amount: number;
    currencyCode?: string;
    locale?: string;
}

/**
 * Format price with locale options
 *
 * @param params - Price formatting parameters
 * @returns Formatted price string (e.g., "1,000,000đ")
 *
 * @example
 * formatPrice({ amount: 1000000, currencyCode: 'VND', locale: 'vi' })
 * // Returns: "1.000.000đ"
 */
export const formatPrice = ({ amount, currencyCode = 'VND', locale = 'vi' }: FormatPriceParams): string => {
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'code',
    });

    if (currencyCode === 'VND') {
        const formattedAmount = formatter.format(amount);
        return formattedAmount.replace('VND', 'đ').replace(/\s/g, '');
    }

    return formatter.format(amount);
};

/**
 * Format price value without currency symbol
 *
 * @param value - Numeric value to format
 * @param locale - Locale for formatting (default: 'vi')
 * @returns Formatted number string
 *
 * @example
 * formatPriceValue(1000000)
 * // Returns: "1.000.000"
 */
export const formatPriceValue = (value: number, locale: string = 'vi'): string => {
    return new Intl.NumberFormat(locale).format(value);
};

/**
 * Format price with discount info
 *
 * @param basePrice - Original price
 * @param salePrice - Sale price
 * @param currencyCode - Currency code
 * @param locale - Locale for formatting
 * @returns Object with formatted price, base price, and discount percentage
 */
export const formatPriceWithDiscount = (
    basePrice: number,
    salePrice: number,
    currencyCode: string = 'VND',
    locale: string = 'vi'
): {
    price: string;
    basePrice: string | null;
    discountPercent: string | null;
} => {
    const hasDiscount = basePrice > salePrice;

    const price = formatPrice({ amount: salePrice, currencyCode, locale });
    const formattedBasePrice = hasDiscount ? formatPrice({ amount: basePrice, currencyCode, locale }) : null;
    const discountPercent = hasDiscount ? `${Math.round(((basePrice - salePrice) / basePrice) * 100)}%` : null;

    return { price, basePrice: formattedBasePrice, discountPercent };
};
